import { QuestionCircleOutlined } from '@ant-design/icons';
import { Popconfirm, Space, Tag } from 'antd';
import Paragraph from 'antd/lib/typography/Paragraph';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import RevxTable, { TableColumnsType } from 'components/RevxTable';
import { Account, PartialAccount } from 'domains/accountDomain';
import { PlatformRoleEnum, ROLE_NAMES, SiteRoleEnum } from 'domains/roleDomain';
import moment from 'moment';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  searchValue: string;
};

export const AccountTable = ({ searchValue }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const loading = useAppSelector(
    (state) => state.loading.effects.account.getPageUser
  );
  const pagedData = useAppSelector((state) => state.account.pagedData);
  const pageSize = useAppSelector((state) => state.account.size);
  const pageNumber = useAppSelector((state) => state.account.page);
  const curItem = useAppSelector((state) => state.account.curItem);
  const accountWithLink = useAppSelector(
    (state) => state.account.accountWithLink
  );

  const onRemove = async (record: PartialAccount) => {
    if (record?.id) {
      await dispatch.account.deleteUser(record);
    }
  };

  const resetPassword = async (record: PartialAccount) => {
    if (record?.id) {
      await dispatch.account.resetPassword(record);
      await dispatch.account.getPageUser();
    }
  };

  const resendActivation = async (record: PartialAccount) => {
    if (record?.id) {
      await dispatch.account.resendActivation(record);
      await dispatch.account.getPageUser();
    }
  };

  const columns: TableColumnsType<Account> = [
    {
      title: t('common.id'),
      width: 60,
      dataIndex: 'id',
    },
    {
      title: t('platform.account.columnTitle.roleName'),
      render: (value, option) => {
        const roleNames: string[] = [];
        if (option.siteRole !== SiteRoleEnum.NoSiteAccess) {
          roleNames.push(ROLE_NAMES[option.siteRole]);
        }
        if (option.platformRole !== PlatformRoleEnum.NoAdminAccess) {
          roleNames.push(ROLE_NAMES[option.platformRole]);
        }
        return roleNames.join(', ');
      },
    },
    {
      title: t('platform.account.columnTitle.email'),
      dataIndex: 'email',
    },
    {
      title: t('platform.account.columnTitle.firstName'),
      dataIndex: 'firstName',
    },
    {
      title: t('platform.account.columnTitle.lastName'),
      dataIndex: 'lastName',
    },
    {
      title: t('platform.account.columnTitle.active'),
      dataIndex: 'active',
      render: (value) => (
        <Tag color={value ? 'green' : 'volcano'}>
          {value ? t('common.active') : t('common.inactive')}
        </Tag>
      ),
    },
    {
      title: 'SSO Account',
      dataIndex: 'isSSOAccount',
      render: (value) => (
        <Tag color={value ? 'blue' : 'default'}>
          {value ? 'SSO' : 'Regular'}
        </Tag>
      ),
    },
    {
      title: t('platform.account.columnTitle.updateTime'),
      dataIndex: 'updateAt',
      render: (value) => (value ? moment(value).format('YYYY-MM-DD') : ''),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      fixed: 'right',
      width: 200,
      render: (value, record) => (
        <Space size="small">
          {record.active ? (
            <Popconfirm
              placement="topRight"
              title={t('common.confirmQuestion')}
              icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
              onConfirm={() => resetPassword(record)}
              okText={t('common.ok')}
              cancelText={t('common.cancel')}
            >
              <RevxButton type="link" key="view" size="small">
                {t('platform.account.action.resetPassword')}
              </RevxButton>
            </Popconfirm>
          ) : (
            <Popconfirm
              placement="topRight"
              title={t('common.confirmQuestion')}
              icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
              onConfirm={() => resendActivation(record)}
              okText={t('common.ok')}
              cancelText={t('common.cancel')}
            >
              <RevxButton type="link" key="view" size="small">
                {t('platform.account.action.activate')}
              </RevxButton>
            </Popconfirm>
          )}

          {accountWithLink && accountWithLink.id === record.id && (
            <Paragraph
              style={{ marginBottom: 0 }}
              copyable={{
                text: accountWithLink.activeLink,
                tooltips: 'Copy emailed link',
              }}
            />
          )}

          <Popconfirm
            placement="topRight"
            title={t('common.confirmQuestion')}
            icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
            onConfirm={() => onRemove(record)}
            okText={t('common.ok')}
            cancelText={t('common.cancel')}
          >
            <RevxButton type="link" key="view" size="small">
              {t('common.remove')}
            </RevxButton>
          </Popconfirm>
          <RevxButton
            type="link"
            key="view"
            size="small"
            onClick={() => dispatch.account.setState({ curItem: record })}
          >
            {t('common.edit')}
          </RevxButton>
        </Space>
      ),
    },
  ];

  const onTableChange = useCallback(
    (pagination, filters, sorter, extra) => {
      switch (extra.action) {
        case 'paginate':
          const page = pagination.current;
          const size = pagination.pageSize;
          dispatch.account.setState({ page, size });
          dispatch.account.getPageUser();
          break;
      }
    },
    [dispatch.account]
  );

  useEffect(() => {
    dispatch.account.getPageUser();
  }, [searchValue, dispatch.account]);

  return (
    <div hidden={!!curItem}>
      <RevxTable<Account>
        loading={loading}
        columns={columns}
        size="small"
        pagination={{
          pageSize: pageSize,
          current: pageNumber,
          total: pagedData?.totalElements,
        }}
        rowClassName={(record: PartialAccount) => {
          if (curItem?.id === record.id) {
            return 'editing';
          }
          return '';
        }}
        indentSize={20}
        dataSource={pagedData?.content || []}
        onChange={onTableChange}
      />
    </div>
  );
};
